import React, { useEffect, useRef, useState } from 'react';
import { Col, convertToKRW, getItemWithExpiration, setItemWithExpiration } from '../../lib/utils';
import dayjs, { Dayjs } from 'dayjs';
import ModalSales from '../Settlement/ModalSales';
import ModalCommission from '../Settlement/ModalCommission';
import ModalVatPolicySetting from '../Settlement/ModalVatPolicySetting';
import SettlementHeader from '../Settlement/SettlementHeader';
import SettlementCalendar from '../Settlement/SettlementCalendar';
import ClassPaySettlementPage from '../Settlement/ClassPaySettlementPage';
import {
    DailyInfo,
    fetchCenterSalesSettlementCalendar,
    fetchCoachNameList,
    fetchGetCenterRemainingScheduleFee,
    fetchGetCenterRemainingScheduleSession,
    SalesSummary,
} from '../../services/useCenterQueryService';
import useMultipleQueries from '../../hook/useMultipleQueries';
import {
    ClassFeeSettlementScheduleRecord,
    fetchGetClassFeeSettlementExcel,
    fetchGetClassFeeSettlementForMembership,
    GetClassFeeSettlementTableDataForMembership,
    MembershipInfo,
} from '../../services/useMemberQueryService';
import ModalClassHistory from '../Settlement/ModalClassHistory';
import ModalMembershipInfo from '../Settlement/ModalMembershipInfo';
import SalesListPage from '../Settlement/SalesListPage';
import {
    fetchGetSalesList,
    MembershipPaymentData,
    SalesManager,
} from '../../services/useMembershipPaymentsQueryService';
import { CodeValueType } from '../../types/types';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import ModalMonthCommision from '../Settlement/ModalMonthCommision';
import SalaryCalculationPage from '../Settlement/SalaryCalculationPage';
import { fetchSalaryCalcs } from '../../services/useSalaryCalcQueryService';
import { SalaryCalcData } from '../../api/salaryCalc';
import {
    buildCoachSalarySummarySheet,
    CoachSalaryExcelValue,
    SALES_LIST_REFERENCE_TOTAL_LABEL,
} from '../../features/salaryCalc/coachSalaryExcelSummary';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import UnpaidListPage from '../Settlement/UnpaidListPage';
import { resolveSalesRecordId } from '../Settlement/salesRecordDetail';
import ModalPartialPayment from '../Settlement/ModalPartialPayment';
import {
    fetchGetUnpaidPayments,
    UnpaidPaymentData,
    UnpaidPaymentTableType,
} from '../../services/useMembershipQueryService';
import { getSalesList } from '../../api/membershipPayments';
import { getClassFeeSettlementExcel } from '@/api/member';
import { withRowMultiplier } from '../Settlement/settlementCommissionFormat';
import {
    buildCategoryNameMap,
    formatAppliedRuleLabel,
    formatMultiplier,
    formatInOutCellText,
    getSettlementCategories,
    hasExceptionRuleRows,
    isExceptionAppliedRule,
    usesServerScheduleFeeInExcel,
    withExceptionValueSource,
    isSettlementTransferLookupError,
    withGroupForcedIn,
    SETTLEMENT_TRANSFER_LOOKUP_FAILED,
    SETTLEMENT_TRANSFER_LOOKUP_FAILED_MESSAGE,
} from '@/features/settlementClassification';
import ModalSettlementInfo from '../../components/modal/ModalSettlementInfo';
import ModalCoachWorkingHours from '../Settlement/ModalCoachWorkingHours';
import { overlay } from 'overlay-kit';
import RappoPaySettlementPage from '../Settlement/RappoPaySettlementPage';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';
import { useReservationRules, useUpdateReservationRules } from '@/features/reservationRules/hooks';
import { formatSessionText, resolveMembershipName } from '../../lib/membershipDisplay';
import SessionReportPage from '../Settlement/SessionReportPage';
import { useSessionReportAvailability } from '@/features/sessionReport/hooks';
import { resolveSessionReportMenuState, SessionReportMenuState } from '@/features/sessionReport/menuVisibility';
import { useEffectOnActive, useKeepAliveContext } from 'keepalive-for-react';

// 매출달력, 수업료 정산, 라포페이 정산, 매출목록, 급여 계산, 미수금 목록, 세션 현황
export type MenuType =
    | 'calendar'
    | 'classSales'
    | 'rappoPaySettlement'
    | 'salesList'
    | 'salaryCalculation'
    | 'unpaidList'
    | 'sessionReport';

/** 공용 토글은 그대로 두고 이 페이지의 설정 문구만 한글로 표시한다. */
const SessionGroupClassToggle = () => {
    const centerId = getItemWithExpiration('authObject')?.centerId;
    const { data: rules, isLoading } = useReservationRules({ centerId });
    const update = useUpdateReservationRules();
    const queryClient = useQueryClient();
    if (!centerId) return null;
    const enabled = !!rules?.includeGroupClassInSettlement;
    return <button type="button" role="switch" aria-checked={enabled} aria-label="그룹수업 합산"
        disabled={!rules || isLoading || update.isLoading} onClick={async () => {
            if (!rules || isLoading || update.isLoading) return;
            await update.mutateAsync({ centerId, body: { includeGroupClassInSettlement: !enabled } });
            queryClient.invalidateQueries();
        }} title={enabled ? '정산·엑셀과 일지 작성률에 그룹수업을 포함해요.' : '정산·엑셀과 일지 작성률에서 그룹수업을 빼요.'}
        className="flex min-h-[44px] items-center gap-[10px] rounded-[8px] bg-[#F2F4F6] px-[12px] text-left text-[15px] disabled:opacity-50">
        <span aria-hidden="true" className={`relative h-[18px] w-[34px] shrink-0 rounded-full ${enabled ? 'bg-[#3D73DD]' : 'bg-[#9BA5B2]'}`}>
            <span className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white ${enabled ? 'left-[18px]' : 'left-[2px]'}`} />
        </span>그룹수업 합산 {enabled ? '켜짐' : '꺼짐'}
    </button>;
};

/** 세션 현황에서만 쓰는 좁은 화면용 탭 줄. 공용 상단 메뉴와 다른 정산 탭은 그대로 둔다. */
export const SessionSettlementHeader = ({ onMenuClick, onCommissionClick, onVatPolicyClick, isRappoPaySettlementMenuVisible }: {
    onMenuClick: (menu: MenuType) => void; onCommissionClick: () => void; onVatPolicyClick: () => void; isRappoPaySettlementMenuVisible: boolean;
}) => {
    const settings = useRef<HTMLDetailsElement>(null);
    useEffect(() => {
        const close = (event: MouseEvent) => { if (settings.current && !settings.current.contains(event.target as Node)) settings.current.open = false; };
        const escape = (event: KeyboardEvent) => { if (event.key === 'Escape' && settings.current?.open) { settings.current.open = false; settings.current.querySelector('summary')?.focus(); } };
        document.addEventListener('click', close); document.addEventListener('keydown', escape);
        return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', escape); };
    }, []);
    const tabs: { key: MenuType; title: string }[] = [
        { key: 'calendar', title: '매출 달력' }, { key: 'classSales', title: '수업료 정산' },
        { key: 'salesList', title: '매출 목록' }, ...(isRappoPaySettlementMenuVisible ? [{ key: 'rappoPaySettlement' as MenuType, title: '라포페이' }] : []),
        { key: 'unpaidList', title: '미수금 목록' }, { key: 'salaryCalculation', title: '급여 계산' }, { key: 'sessionReport', title: '세션 현황' },
    ];
    const openSetting = (callback: () => void) => { if (settings.current) settings.current.open = false; callback(); };
    return <div data-testid="session-settlement-header" className="relative mt-[40px] mb-[33px] flex w-full min-w-0 items-center justify-between gap-[20px]">
        <nav aria-label="매출 정산 메뉴" className="flex min-w-0 flex-wrap items-center gap-x-[28px] gap-y-[12px]">
            {tabs.map(({ key, title }) => <button key={key} type="button" onClick={() => onMenuClick(key)} aria-current={key === 'sessionReport' ? 'page' : undefined}
                className={`whitespace-nowrap border-0 border-b-[4px] border-solid bg-[transparent] pb-[8px] text-[22px] font-semibold ${key === 'sessionReport' ? 'border-[#3D4244] text-[#3D4244]' : 'border-[transparent] text-[#6B7280]'}`}>{title}</button>)}
        </nav>
        <details ref={settings} className="relative shrink-0">
            <summary className="flex h-[44px] cursor-pointer list-none items-center rounded-[10px] bg-[#2D3132] px-[18px] text-[15px] text-white">설정 ▾</summary>
            <div className="absolute right-0 top-full z-30 mt-[8px] flex w-[270px] flex-col gap-[8px] rounded-[12px] border border-solid border-[#E1E6ED] bg-white p-[12px] shadow-lg">
                <SessionGroupClassToggle />
                <button type="button" onClick={() => openSetting(onVatPolicyClick)} className="min-h-[44px] rounded-[8px] px-[12px] text-left text-[15px] hover:bg-[#F2F4F6]">부가세 설정</button>
                <button type="button" onClick={() => openSetting(onCommissionClick)} className="min-h-[44px] rounded-[8px] px-[12px] text-left text-[15px] hover:bg-[#F2F4F6]">수업 정산 비율 설정</button>
            </div>
        </details>
    </div>;
};

const settlementMenuTypes: MenuType[] = [
    'calendar',
    'classSales',
    'rappoPaySettlement',
    'salesList',
    'salaryCalculation',
    'unpaidList',
    'sessionReport',
];

const isSettlementMenuType = (value: string | null): value is MenuType =>
    !!value && settlementMenuTypes.includes(value as MenuType);

/**
 * 세션 현황 탭은 정산 권한이 있는 모든 센터에서, 서버에 기능이 있을 때 보인다
 * (resolveSessionReportMenuState). 판정 중(`unknown`)에는 주소의 `?tab=sessionReport` 를 그대로 두고
 * (새로고침해도 그 탭에 머문다), 숨김(`unavailable`)으로 정해지면 매출 달력으로 보낸다.
 */

const resolveSettlementMenu = (
    value: string | null,
    isRappoPaySettlementMenuVisible: boolean,
    sessionReportMenuState: SessionReportMenuState = 'unknown',
): MenuType => {
    if (!isSettlementMenuType(value)) return 'calendar';
    if (value === 'rappoPaySettlement' && !isRappoPaySettlementMenuVisible) return 'calendar';
    if (value === 'sessionReport' && sessionReportMenuState === 'unavailable') return 'calendar';
    return value;
};

const hasEffectiveCommissionRate = (item: any) =>
    item?.effectiveCommissionRate !== undefined && item?.effectiveCommissionRate !== null;

const formatExcelCommissionValue = (item: any) => {
    if (hasEffectiveCommissionRate(item)) {
        return Math.round(Number(item.effectiveCommissionRate) * 10) / 10;
    }
    if (item.isCompositeRate) return '복합';
    if (item.commission === 0) return '0';
    return item.commission ? item.commission : '0';
};

const SettlementPage = ({ createAlert }: { createAlert?: any }) => {
    const [authObject, setAuthObject] = useState(getItemWithExpiration('authObject'));
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));
    const { data: reservationRules } = useReservationRules({ centerId: authObject?.centerId || '' });
    const includeGroupClassInSettlement = !!reservationRules?.includeGroupClassInSettlement;
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();

    const [date, setDate] = useState(dayjs().toISOString());
    // 알림센터 「라포 확인」 카드: 이동 state 의 달·강사로 세션 현황을 연다. 같은 이동(location.key)은 한 번만 적용한다.
    /**
     * KeepAliveRouteOutlet 은 검색 문자열까지 캐시 키로 쓰므로 탭마다 정산 화면 인스턴스가 따로 살아 있고, 숨은 인스턴스도
     * location 변화를 받는다. 이동 state(알림 카드·급여 보기)는 지금 보이는(active) 인스턴스만 적용한다
     * (검증 P2: 숨은 매출 달력 화면의 달까지 바뀌던 것). 정산 화면은 항상 KeepAlive 안에서 그려진다.
     */
    const { active: isVisibleInstance } = useKeepAliveContext();
    const [sessionReportFocus, setSessionReportFocus] = useState<{ coachId?: string; nonce: string } | null>(null);
    const consumedFocusKey = useRef<string | null>(null);
    useEffect(() => {
        if (location.pathname !== '/settlement' || !isVisibleInstance || consumedFocusKey.current === location.key) return;
        const focus = (location.state as { sessionReportFocus?: { yearMonth?: unknown; coachId?: unknown } } | null)?.sessionReportFocus;
        if (!focus || typeof focus.yearMonth !== 'string' || !/^\d{4}-(0[1-9]|1[0-2])$/.test(focus.yearMonth)) return;
        consumedFocusKey.current = location.key;
        setDate(dayjs(`${focus.yearMonth}-01`).toISOString());
        setSessionReportFocus({
            coachId: typeof focus.coachId === 'string' && focus.coachId ? focus.coachId : undefined,
            nonce: location.key,
        });
    }, [location.key, location.pathname, location.state, isVisibleInstance]);
    const isRappoPaySettlementMenuVisible = authObject?.adminId === 'trial';
    // 세션 현황 탭: 정산 권한이 있는 모든 센터에 보인다(10/19). 서버 기능 사전 조회만 본다(구서버·권한 없음이면 숨김).
    const sessionReportAvailability = useSessionReportAvailability({ centerId: authObject?.centerId });
    // 화면 유지(KeepAlive)로 다시 들어오면 마운트가 일어나지 않는다. 일시 실패 상태면 들어올 때 다시 묻는다.
    useEffectOnActive(
        () => {
            if (sessionReportAvailability.isError && !sessionReportAvailability.isFetching) {
                sessionReportAvailability.refetch();
            }
        },
        [],
        true,
    );
    const sessionReportMenuState: SessionReportMenuState = resolveSessionReportMenuState({
        probe: { data: sessionReportAvailability.data, isError: sessionReportAvailability.isError },
    });
    // 매출 달력, 수업료 정산, 매출 목록 화면
    const [selectedMenu, setSelectedMenu] = useState<MenuType>(() =>
        resolveSettlementMenu(
            new URLSearchParams(window.location.search).get('tab'),
            isRappoPaySettlementMenuVisible,
            sessionReportMenuState,
        ),
    );

    const updateSelectedMenu = React.useCallback(
        (menu: MenuType, options?: { replace?: boolean }) => {
            const nextMenu = resolveSettlementMenu(menu, isRappoPaySettlementMenuVisible, sessionReportMenuState);
            setSelectedMenu(nextMenu);
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('tab', nextMenu);
                    return next;
                },
                { replace: options?.replace },
            );
        },
        [isRappoPaySettlementMenuVisible, sessionReportMenuState, setSearchParams],
    );

    useEffect(() => {
        const menuFromQuery = resolveSettlementMenu(
            searchParams.get('tab'),
            isRappoPaySettlementMenuVisible,
            sessionReportMenuState,
        );
        if (menuFromQuery !== selectedMenu) {
            setSelectedMenu(menuFromQuery);
        }
    }, [isRappoPaySettlementMenuVisible, sessionReportMenuState, searchParams, selectedMenu]);

    // 다른 탭으로 나가면 카드 이동 표시를 지워, 세션 현황에 다시 들어올 때 강사를 또 고르지 않게 해요.
    // 세션 현황에서 나갈 때만 지운다(들어오는 순간 같은 렌더에서 지우면 카드 이동이 사라진다).
    const previousMenu = useRef(selectedMenu);
    useEffect(() => {
        if (previousMenu.current === 'sessionReport' && selectedMenu !== 'sessionReport') setSessionReportFocus(null);
        previousMenu.current = selectedMenu;
    }, [selectedMenu]);

    useEffect(() => {
        if (sessionReportMenuState === 'unavailable' && selectedMenu === 'sessionReport') {
            updateSelectedMenu('calendar', { replace: true });
        }
    }, [sessionReportMenuState, selectedMenu, updateSelectedMenu]);

    useEffect(() => {
        if (!isRappoPaySettlementMenuVisible && selectedMenu === 'rappoPaySettlement') {
            updateSelectedMenu('calendar', { replace: true });
        }
    }, [isRappoPaySettlementMenuVisible, selectedMenu, updateSelectedMenu]);
    // 선택된 강사
    const [selectedCalendarCoach, setSelectedCalendarCoach] = useState<CodeValueType | 'all'>('all');

    /**
     *
     * 수업료 정산 화면
     * 선택된 수업기록
     */
    // 강사 선택
    const [selectedCoach, setSelectedCoach] = React.useState('all');

    const [startDate, setStartDate] = useState(dayjs(date).startOf('month').toISOString());
    const [endDate, setEndDate] = useState(dayjs(date).endOf('month').add(1, 'ms').toISOString());
    // 선택한 날짜
    const [selectedDate, setSelectedDate] = useState<Dayjs | string | undefined>(undefined);

    /**
     * 매출목록 화면
     *
     */

    // 판매담당자 선택
    const [selectedSalesManager, setSelectedSalesManager] = React.useState<SalesManager>({
        userId: 'all',
        role: 'all',
        userName: 'all',
    });
    // 회원별인지 수업별인지 선택
    // 정산 모드(base/tier/sessionTier) 무관하게 '수강권별'이 기본이다.
    // tier만 'class'로 강제하던 분기는 제거했다 — tier도 수강권별 탭을 지원한다.
    const [groupType, setGroupType] = React.useState<'class' | 'member'>('member');

    // 매출목록 선택 날짜
    const [selectedSalesListDate, setSelectedSalesListDate] = useState<Dayjs | string | undefined>(undefined);

    /**
     * 급여계산 화면
     * 선택된 강사
     */

    const [selectedSalaryCoach, setSelectedSalaryCoach] = useState<CodeValueType | undefined>(undefined);
    /**
     * 미수금 목록
     * 선택된 강사
     */

    const [selectedUnpaidCoach, setSelectedUnpaidCoach] = useState<CodeValueType | undefined>({
        code: 'all',
        value: '모든 강사',
    });

    const openedSettlementInfoModalRef = React.useRef(false);

    const getOverlayCloseSetter = (unmount: () => void): React.Dispatch<React.SetStateAction<boolean>> => {
        return () => unmount();
    };

    // type : 매출금액, 결제 수업수 , 완료한 수업수 영어로
    const onClickEvent = (event: DailyInfo, type: 'salesAmount' | 'paymentCount' | 'completedCount') => {
        overlay.open(({ unmount }) => (
            <ModalSales
                event={{ ...event, type }}
                setIsVisible={getOverlayCloseSetter(unmount)}
                onClickDetail={onClickDetail}
            />
        ));
    };

    const onMonthNavigate = (date: Date | string | Dayjs) => {
        // 수업료 정산화면일때 선택된 날짜 초기화
        // 미래의 달은 못넘어감
        if (selectedMenu === 'classSales') {
            setSelectedDate(undefined);
            // 파라미터 date 가 오늘의 달보다 미래의 달이면 return
            if (dayjs(date).isAfter(dayjs(), 'month')) {
                return;
            } else {
                setDate(date as string);
            }
        } else {
            setDate(date as string);
        }
    };

    const { refinedResults, isLoading, refresh } = useMultipleQueries([
        // 0 : 수업료 정산
        {
            fetchFn: fetchGetClassFeeSettlementForMembership,
            params: {
                centerId: authObject.centerId,
                startDate: startDate,
                endDate: endDate,
                selectedCoach: selectedCoach,
                accessToken: authorization,
            },
            enabled: !!authObject?.centerId,
            suspense: false,
        },
        // 1 : 매출목록
        {
            fetchFn: fetchGetSalesList,
            params: {
                centerId: authObject.centerId,
                startDate: startDate,
                endDate: endDate,
                userId: selectedSalesManager.userId,
                role: selectedSalesManager.role,
                accessToken: authorization,
            },
            enabled: !!authObject?.centerId,
            suspense: false,
        },
        // 2 : 강사 리스트 (퇴사 강사 포함)
        {
            fetchFn: fetchCoachNameList,
            params: { centerId: authObject?.centerId, isAllConcat: true, includeDeleted: true },
            enabled: !!authObject?.centerId,
            suspense: true,
        },

        {
            fetchFn: fetchGetCenterRemainingScheduleFee,
            params: {
                centerId: authObject?.centerId,
                targetMonth: dayjs(date).format('YYYY-MM'),
                targetCoach: selectedCalendarCoach === 'all' ? 'all' : selectedCalendarCoach.code,
            },
            enabled: !!authObject?.centerId,
            suspense: false,
        },

        {
            fetchFn: fetchGetCenterRemainingScheduleSession,
            params: {
                centerId: authObject?.centerId,
                targetMonth: dayjs(date).format('YYYY-MM'),
                targetCoach: selectedCoach === 'all' ? 'all' : selectedCoach,
            },
            enabled: !!authObject?.centerId,
            suspense: false,
        },
        {
            fetchFn: fetchCenterSalesSettlementCalendar,
            params: {
                centerId: authObject?.centerId,
                targetMonth: dayjs(date).format('YYYY-MM'),
                targetCoach: selectedCalendarCoach === 'all' ? 'all' : selectedCalendarCoach.code,
            },
            enabled: !!authObject?.centerId && !!date && !!selectedCalendarCoach,
            suspense: false,
        },
        // 6 : 급여 계산
        {
            fetchFn: fetchSalaryCalcs,
            params: {
                year: dayjs(date).year(),
                month: dayjs(date).month() + 1,
                selectedCoachId: selectedSalaryCoach?.code,
                centerId: authObject?.centerId,
                accessToken: authorization,
            },
            enabled: !!authObject?.centerId && !!date && !!selectedSalaryCoach,
            suspense: false,
        },
        // 7 : 미수금 목록
        {
            fetchFn: fetchGetUnpaidPayments,
            params: {
                userId: selectedUnpaidCoach?.code,
                role: selectedUnpaidCoach?.object?.role ?? 'all',
                centerId: authObject?.centerId,
                accessToken: authorization,
            },
            enabled: !!authObject?.centerId,
            suspense: false,
        },
        // // 8 : 엑셀 다운 할때 수업료 정산 , selectedSalaryCoach
        // {
        //     fetchFn: fetchGetClassFeeSettlementExcel,
        //     params: {
        //         centerId: authObject?.centerId,
        //         startDate,
        //         endDate,
        //         selectedCoach: selectedSalaryCoach?.code,
        //     },
        //     enabled: !!authObject?.centerId,
        //     suspense: false,
        // },

        // 판매 담당자 리스트
        // {
        //     fetchFn: fetchGetSalesManagers,
        //     params: { centerId: authObject?.centerId },
        //     enabled: !!authObject?.centerId,
        //     suspense: false,
        // },
    ]);

    const classSettlementData = refinedResults?.[0]?.data as any;
    const salesListData = refinedResults?.[1]?.data as any;
    const coachList: CodeValueType<string>[] = refinedResults?.[2]?.data as CodeValueType<string>[];
    const totalRemainingScheduleFee = refinedResults?.[3]?.data as any;

    const totalRemainingScheduleSession = refinedResults?.[4]?.data as any;
    const calendar = refinedResults?.[5]?.data as SalesSummary;
    const salaryCalcData = refinedResults?.[6]?.data as SalaryCalcData;
    const unpaidListData = refinedResults?.[7]?.data as UnpaidPaymentData;
    // 재등록 수업료용 양도 조회 실패(503) — 그 화면은 0원 대신 오류 상태로 보인다.
    const lookupErrorOf = (index: number) =>
        isSettlementTransferLookupError(refinedResults?.[index]?.error) ? SETTLEMENT_TRANSFER_LOOKUP_FAILED_MESSAGE : undefined;
    const classSettlementLoadError = lookupErrorOf(0);
    const calendarLoadError = lookupErrorOf(5);
    const salaryLoadError = lookupErrorOf(6);

    /**
     * PATCH 응답을 잃었을 수 있을 때는 화면에 남은 행을 믿지 않고, 같은 달의 전체 매출 쿼리를
     * 반드시 다시 실행해 결제 행의 현재 판매자를 확인한다. 판매자 필터가 걸린 쿼리만 새로
     * 불러오면 이미 새 판매자로 바뀐 행이 결과에서 사라질 수 있어 `all/all` 범위를 사용한다.
     */
    const refetchSalesRecord = React.useCallback(
        async (record: MembershipPaymentData): Promise<MembershipPaymentData> => {
            const recordId = resolveSalesRecordId(record);
            if (!recordId || !authObject?.centerId || !authorization) {
                throw new Error('SALES_RECORD_REFRESH_CONTEXT_MISSING');
            }

            const query = fetchGetSalesList(
                {
                    centerId: authObject.centerId,
                    startDate,
                    endDate,
                    userId: 'all',
                    role: 'all',
                },
                authorization,
            );
            let response: any;
            if (queryClient.getQueryState(query.queryKey)) {
                await queryClient.refetchQueries(
                    { queryKey: query.queryKey, exact: true, type: 'all' },
                    { throwOnError: true },
                );
                response = queryClient.getQueryData(query.queryKey);
            } else {
                response = await queryClient.fetchQuery({ ...query, staleTime: 0 });
            }
            const rows = response?.data?.tableDataByMembershipPayment;
            const refreshedRecord = Array.isArray(rows)
                ? rows.find((row: MembershipPaymentData) => resolveSalesRecordId(row) === recordId)
                : undefined;

            if (!refreshedRecord) throw new Error('SALES_RECORD_NOT_FOUND_AFTER_REFRESH');
            return refreshedRecord;
        },
        [authObject?.centerId, authorization, endDate, queryClient, startDate],
    );

    // const salesManagerList = refinedResults?.[3]?.data;

    useEffect(() => {
        setStartDate(dayjs(date).startOf('month').toISOString());
        setEndDate(dayjs(date).endOf('month').add(1, 'ms').toISOString());
    }, [date, selectedCoach, selectedSalesManager]);

    // 달력에서 자세히 보기 눌렀을때
    // - [ ] 매출금액 모달 -> 자세히보기 매출목록 날짜까지 물고
    // - [ ] 결제수업수 모달 -> 자세히보기 매출목록 날짜까지 물고
    // - [ ] 잡힌 수업수 모달 -> 자세히보기 수업별 날짜물고
    const onClickDetail = (event: DailyInfo, type: MenuType) => {
        setDate(event.start);
        if (type === 'classSales') {
            setGroupType('class');
            if (selectedCalendarCoach !== 'all') {
                const coachString = coachList?.find((coach) => coach?.code === selectedCalendarCoach?.code)?.code;
                setSelectedCoach(coachString ?? 'all');
            } else {
                setSelectedCoach('all');
            }
        } else if (type === 'salesList') {
            // 강사가 있을때 강사 선택
            if (selectedCalendarCoach !== 'all') {
                const result = salesListData.salesManagers.find(
                    (manager: SalesManager) => manager.userId === selectedCalendarCoach.code,
                );
                setSelectedSalesManager({
                    userId: result.userId,
                    role: result.role,
                    userName: result.userName,
                });
            } else {
                setSelectedSalesManager({
                    userId: 'all',
                    role: 'all',
                    userName: 'all',
                });
            }
        }
        setStartDate(dayjs(event.start).startOf('day').toISOString());
        setEndDate(dayjs(event.start).endOf('day').toISOString());
        setSelectedDate(dayjs(event.start).startOf('day').toISOString());
        setSelectedSalesListDate(dayjs(event.start).startOf('day').toISOString());
        updateSelectedMenu(type);
    };

    useEffect(() => {
        if (window.location.pathname === '/settlement') {
            if (selectedMenu === 'calendar') {
                //
                refresh(5);
                refresh(4);
            } else if (selectedMenu === 'rappoPaySettlement') {
                // 추후 라포페이 정산 API 연동 예정
            } else if (selectedMenu === 'classSales') {
                refresh(0);
                refresh(3);
                // 포커싱 되었을 때 실행할 로직
            } else if (selectedMenu === 'salesList') {
                refresh(1);
                // 포커싱 되었을 때 실행할 로직
            } else if (selectedMenu === 'salaryCalculation') {
                refresh(6);
                // 포커싱 되었을 때 실행할 로직
            } else if (selectedMenu === 'unpaidList') {
                refresh(7);
                // 포커싱 되었을 때 실행할 로직
            }
        } else {
            // 포커싱 아웃 됐을때 실행할 로직
        }
    }, [location, selectedMenu]);

    /**
     * 세션 현황 「이 강사 급여 보기 ›」: 이동 state(salaryFocus)의 달·강사로 급여 계산 탭을 연다.
     * ?tab= 이 바뀌면 KeepAliveRouteOutlet 이 검색 문자열까지 캐시 키로 써서 정산 화면이 새로 마운트될 수 있다 —
     * 그래서 컴포넌트 상태가 아니라 이동 state 로 넘기고, 받은 화면이 같은 이동(location.key)을 한 번만 적용한다
     * (알림 카드 sessionReportFocus 와 같은 방식). 강사 목록이 아직이면 목록이 들어올 때 그 강사를 고른다.
     */
    const pendingSalaryCoachIdRef = useRef<string | null>(null);
    const consumedSalaryFocusKey = useRef<string | null>(null);
    useEffect(() => {
        if (location.pathname !== '/settlement' || !isVisibleInstance || consumedSalaryFocusKey.current === location.key) return;
        const focus = (location.state as { salaryFocus?: { yearMonth?: unknown; coachId?: unknown } } | null)?.salaryFocus;
        if (!focus || typeof focus.yearMonth !== 'string' || !/^\d{4}-(0[1-9]|1[0-2])$/.test(focus.yearMonth)) return;
        consumedSalaryFocusKey.current = location.key;
        setDate(dayjs(`${focus.yearMonth}-01`).toISOString());
        const coachId = typeof focus.coachId === 'string' && focus.coachId ? focus.coachId : null;
        pendingSalaryCoachIdRef.current = coachId;
        const coach = coachId ? coachList?.find((item) => item.code === coachId) : undefined;
        if (coach) setSelectedSalaryCoach(coach);
    }, [location.key, location.pathname, location.state, coachList, isVisibleInstance]);

    // coachList 가 데이터가 비동기로 들어오면 setSelectedSalaryCoach (급여 보기 이동으로 받은 강사가 있으면 그 강사)
    useEffect(() => {
        if (!refinedResults[2].isLoading && coachList && coachList.length > 0) {
            const pendingId = pendingSalaryCoachIdRef.current;
            pendingSalaryCoachIdRef.current = null;
            const pending = pendingId ? coachList.find((item) => item.code === pendingId) : undefined;
            setSelectedSalaryCoach(pending ?? coachList[0]);
        }
    }, [refinedResults[2].isLoading]);

    // useEffect(() => {
    //     if (openedSettlementInfoModalRef.current) return;
    //     const hasSeenInfo = localStorage.getItem('hasSeenSettlementInfo');
    //     if (hasSeenInfo === 'true') return;

    //     openedSettlementInfoModalRef.current = true;

    //     overlay.open(({ unmount }) => (
    //         <ModalSettlementInfo
    //             setIsVisible={() => {
    //                 localStorage.setItem('hasSeenSettlementInfo', 'true');
    //                 unmount();
    //             }}
    //         />
    //     ));
    // }, []);

    const openVatPolicyModal = () => {
        overlay.open(({ unmount }) => (
            <ModalVatPolicySetting
                onClose={() => {
                    setAuthObject(getItemWithExpiration('authObject'));
                    unmount();
                }}
            />
        ));
    };

    // afterClose: 급여 계산 「구간 미설정 · 설정하기」에서 열었을 때 닫은 뒤 급여 숫자를 다시 받는다(설정 창은 급여 조회를 무효화하지 않는다).
    const showCommissionModal = (afterClose?: () => void) => {
        overlay.open(({ unmount }) => (
            <ModalCommission
                setIsVisible={(visible) => {
                    if (!visible) {
                        setAuthObject(getItemWithExpiration('authObject'));
                        afterClose?.();
                    }
                    unmount();
                }}
                date={date}
                onWorkingHoursClick={openWorkingHoursModal}
            />
        ));
    };
    const openCommissionModal = () => showCommissionModal();

    const openWorkingHoursModal = () => {
        overlay.open(({ unmount }) => (
            <ModalCoachWorkingHours
                setIsVisible={getOverlayCloseSetter(unmount)}
                date={date}
                onApplySuccess={() => {
                    refresh(0); // 0 : 수업료 정산 (fetchGetClassFeeSettlementForMembership)
                    refresh(6); // 6 : 급여 계산 (fetchSalaryCalcs)
                }}
            />
        ));
    };

    const openClassHistoryModal = React.useCallback((scheduleRecord: ClassFeeSettlementScheduleRecord) => {
        overlay.open(({ unmount }) => (
            <ModalClassHistory setIsVisible={getOverlayCloseSetter(unmount)} scheduleRecord={scheduleRecord} />
        ));
    }, []);

    const openMembershipInfoModal = React.useCallback((membershipInfo: MembershipInfo) => {
        overlay.open(({ unmount }) => (
            <ModalMembershipInfo setIsVisible={getOverlayCloseSetter(unmount)} membershipInfo={membershipInfo} />
        ));
    }, []);

    const openMonthCommissionModal = React.useCallback(
        (row: GetClassFeeSettlementTableDataForMembership) => {
            overlay.open(({ unmount }) => (
                <ModalMonthCommision
                    setIsVisible={getOverlayCloseSetter(unmount)}
                    row={row}
                    year={dayjs(date).year()}
                    month={dayjs(date).month() + 1}
                    onPatchMonthCommissionSuccess={() => {
                        refresh(0);
                    }}
                />
            ));
        },
        [date, refresh],
    );

    const openPartialPaymentModal = React.useCallback(
        (row: UnpaidPaymentTableType) => {
            overlay.open(({ unmount }) => (
                <ModalPartialPayment
                    setIsVisible={getOverlayCloseSetter(unmount)}
                    unpaidRowData={row}
                    createAlert={createAlert}
                    onPostSplitPaymentSuccess={() => {
                        refresh(7);
                    }}
                />
            ));
        },
        [createAlert, refresh],
    );

    const getMenuWidth = () => {
        return selectedMenu === 'sessionReport' ? 'w-full max-w-[1395px] px-[24px]' : 'w-[1395px]';
    };

    const excelExport = async (value: CoachSalaryExcelValue, coachId: string) => {
        const vatPolicy = authObject?.vatPolicy ?? 'inclusive';
        const vatRate = authObject?.vatRate ?? 10;
        const isVatExclusive = vatPolicy === 'exclusive';

        // 엑셀 다운로드 시점에서 최신 데이터 조회
        const excelData = await getClassFeeSettlementExcel({
            centerId: authObject?.centerId,
            startDate,
            endDate,
            selectedCoach: selectedSalaryCoach?.code ?? 'all',
            accessToken: authorization,
        });
        // 재등록 수업료용 양도 조회 실패(503) — 수업 내역이 빠진 엑셀을 만들지 않는다.
        if (excelData?.status === 503 && excelData?.errorCode === SETTLEMENT_TRANSFER_LOOKUP_FAILED) {
            createAlert?.('엑셀을 만들지 못했어요', SETTLEMENT_TRANSFER_LOOKUP_FAILED_MESSAGE, true);
            return;
        }

        const targetManager = salesListData?.salesManagers?.find((manager: any) => manager.userId === coachId);

        let salesList: any[] = [];
        if (targetManager) {
            const res = await getSalesList({
                centerId: authObject?.centerId,
                startDate,
                endDate,
                userId: targetManager.userId,
                role: targetManager.role,
                accessToken: authorization,
            });
            salesList = res?.data?.tableDataByMembershipPayment || [];
        }

        const classFeeDataForClass = excelData?.data?.tableDataBySchedule?.filter(
            (item: any) => item.coachId === coachId,
        );

        // 요약 칸(매출·인센티브·수업료·기본급·공제·기타 수당·최종 급여)은 화면이 넘긴 computeSalary 결과를 숫자로 쓴다.
        // 수식으로 다시 만들지 않는다 — 엑셀 최종 급여 = 화면 최종 급여.
        const headerSheet = buildCoachSalarySummarySheet(value);
        XLSX.utils.sheet_add_aoa(
            headerSheet,
            [
                [
                    `정산 기준: ${dayjs(date).format('YYYY년 MM월')} 월 전체 수업(예정 포함) · 그룹수업 합산 ${
                        includeGroupClassInSettlement ? 'ON' : 'OFF'
                    }`,
                ],
            ],
            { origin: 'A4' },
        );

        // 두 가지 정산 옵션을 동시에 지원:
        //  1) isVatExclusive  → '수업 금액(부가세제외)' 컬럼 추가
        //  2) isSessionTier   → 'IN/OUT' 제거 + '누적 총 회차' 추가
        // 정산액(G열, index 6)과 정산 타입(마지막 컬럼)은 모든 조합에서 위치/이름 동일.
        const isSessionTier = authObject?.settlementType === 'sessionTier';
        /**
         * 「적용 규칙」 열 — 예외(분류·재등록)가 적용된 행이 있을 때만 맨 끝에 붙인다(그 밖엔 엑셀 모양 그대로).
         * 예외 수업료는 세 정산 방식 모두에서 계산되므로 방식은 보지 않는다.
         * 예외 규칙 수업의 정산액은 아래 수식(수업 금액 × 정산 비율)으로 다시 만들 수 없다
         * — 배수 규칙 행의 정산 비율은 원래 요율이라 수식이 배수를 빠뜨리고, 기본 정산 요율 예외 출석 행은 서버가 반올림한다.
         *   그래서 서버 값을 그대로 쓴다(usesServerScheduleFeeInExcel).
         */
        const showAppliedRule = hasExceptionRuleRows(classFeeDataForClass);
        let excelCategoryMap = buildCategoryNameMap([]);
        if (showAppliedRule && classFeeDataForClass?.some((item: any) => String(item?.appliedRule).startsWith('category:'))) {
            try {
                const categoriesState = await getSettlementCategories(authObject?.centerId);
                excelCategoryMap = buildCategoryNameMap(categoriesState.categories);
            } catch {
                // 이름을 못 읽어도 내보내기는 막지 않는다 — 칸에는 「알 수 없는 분류」가 남는다.
            }
        }
        const classHeaders: string[] = [
            '강사',
            '회원',
            ...(isSessionTier ? [] : ['IN/OUT']),
            '수업 일시',
            '수강권',
            '회차/총 회차',
            ...(isSessionTier ? ['누적 총 회차'] : []),
            '정산액',
            ...(isVatExclusive ? ['수업 금액(부가세제외)'] : []),
            '수업 금액',
            '정산 비율',
            '정산 타입',
            ...(showAppliedRule ? ['적용 규칙'] : []),
        ];
        const classData: any[][] = [];
        const classDataUseServerScheduleFee: boolean[] = [];
        const pushClassDataRow = (row: any[], useServerScheduleFee = false) => {
            classData.push(row);
            classDataUseServerScheduleFee.push(useServerScheduleFee);
        };

        classFeeDataForClass.forEach((item: any) => {
            // 배수 예외 행은 정산 비율이 구간표 요율이다 — '복합'처럼 글자로 배수를 함께 적는다.
            // 이 행의 정산액은 아래에서 수식이 아니라 서버 값을 쓰므로(isExceptionAppliedRule) 글자여도 계산이 깨지지 않는다.
            const multiplierText = formatMultiplier(item.exceptionMultiplier);
            // 「항상 IN 요율의 배수」 행은 「40% (IN ×1.2)」 — 정산 비율이 IN 요율이다(예외 행이라 정산액은 서버 값).
            const commissionVal =
                item.exceptionMode === 'inMultiplier' && isExceptionAppliedRule(item.appliedRule)
                    ? `${formatExcelCommissionValue(item)}${withRowMultiplier(item)}`
                    : multiplierText && isExceptionAppliedRule(item.appliedRule)
                      ? `${formatExcelCommissionValue(item)} ${multiplierText}`
                      : formatExcelCommissionValue(item);
            const commissionTypeText = hasEffectiveCommissionRate(item)
                ? '정률제'
                : item.commissionType === 'fixed'
                  ? '정액제'
                  : '정률제';
            const isGroupRow = item.source === 'group' && Array.isArray(item.members) && item.members.length > 0;

            if (isGroupRow) {
                pushClassDataRow(
                    [
                        item.coachName,
                        item.memberName,
                        ...(isSessionTier ? [] : ['']),
                        dayjs(item.startTime).format('YYYY-MM-DD(ddd) HH:mm'),
                        resolveMembershipName(item) || '-',
                        '-',
                        ...(isSessionTier ? [item.cumulativeSessionCount ?? '-'] : []),
                        item.memberScheduleFee ?? 0,
                        ...(isVatExclusive ? [''] : []),
                        item.pricePerSchedule ?? 0,
                        commissionVal,
                        commissionTypeText,
                        ...(showAppliedRule ? [''] : []),
                    ],
                    true,
                );

                item.members.forEach((member: any) => {
                    const attendanceText =
                        member.attendanceStatus === 'ATTENDANCE'
                            ? '출석'
                            : member.attendanceStatus === 'PENALTY_CANCELLED'
                              ? '패널티'
                              : '노쇼';
                    pushClassDataRow(
                        [
                            '',
                            `  └ ${member.memberName} (${withGroupForcedIn(attendanceText, member)})`,
                            ...(isSessionTier ? [] : ['']),
                            '',
                            resolveMembershipName(member) || '-',
                            formatSessionText(member),
                            ...(isSessionTier ? [member.cumulativeSessionCount ?? '-'] : []),
                            '',
                            ...(isVatExclusive ? [''] : []),
                            member.pricePerSchedule ?? '',
                            '',
                            '',
                            ...(showAppliedRule ? [''] : []),
                        ],
                        true,
                    );
                });
                return;
            }

            pushClassDataRow([
                item.coachName,
                item.memberName,
                ...(isSessionTier ? [] : [formatInOutCellText(item)]),
                dayjs(item.startTime).format('YYYY-MM-DD(ddd) HH:mm'),
                resolveMembershipName(item) || '-',
                formatSessionText(item),
                ...(isSessionTier ? [item.cumulativeSessionCount ?? '-'] : []),
                item.memberScheduleFee ?? 0, // 정산액 (G열)
                ...(isVatExclusive ? [''] : []), // 수업 금액(부가세제외) — 아래에서 수식으로 채움
                item.pricePerSchedule ?? 0, // 수업 금액
                commissionVal, // 정산 비율
                commissionTypeText, // 정산 타입
                ...(showAppliedRule
                    ? [
                          withExceptionValueSource(
                              formatAppliedRuleLabel(
                                  item.appliedRule,
                                  excelCategoryMap,
                                  item.exceptionMultiplier,
                                  item.exceptionMode,
                              ) ?? '-',
                              item,
                          ),
                      ]
                    : []),
            ], usesServerScheduleFeeInExcel(item));
        });
        const resultIndex = classHeaders.indexOf('정산액');
        const vatExcludedIndex = classHeaders.indexOf('수업 금액(부가세제외)');
        const classFeeIndex = classHeaders.indexOf('수업 금액');
        const commissionIndex = classHeaders.indexOf('정산 비율');
        const typeIndex = classHeaders.indexOf('정산 타입');
        const cellRef = (columnIndex: number, rowIndex: number) =>
            `${XLSX.utils.encode_col(columnIndex)}${rowIndex}`;

        const salesListHeaders = [
            '판매상태',
            '결제일시',
            '판매담당자',
            '담당강사',
            '회원',
            '결제금액',
            '취소액',
            '총회차',
            '수강권',
            '이용기간',
        ];
        const _salesListData = salesList?.map((item: any) => [
            item.saleStatus === 'COMPLETED'
                ? '완료'
                : item.saleStatus === 'UPGRADED'
                  ? '업그레이드'
                  : item.saleStatus === 'PARTIAL_REFUND'
                    ? '부분 환불'
                    : item.saleStatus === 'PARTIAL_PAYMENT'
                      ? '분할 결제'
                      : '전체 환불',
            dayjs(item.paymentDate).format('YYYY-MM-DD'),
            item.salesManager.userName,
            item.coachName,
            item.memberName,
            item.paymentAmount,
            item.cancelAmount,
            (() => {
                const totalValue = item.totalSession ?? item.totalCount ?? '-';
                const offsetValue = item.totalSessionOffset ?? item.totalCountOffset ?? item.totalSessionChange ?? 0;
                const isRefund = item.saleStatus === 'FULL_REFUND' || item.saleStatus === 'PARTIAL_REFUND';
                const isUpgrade = item.saleStatus === 'UPGRADED';
                const offsetText = !offsetValue ? '' : isRefund ? `(-${offsetValue})` : isUpgrade ? `(+${offsetValue})` : '';
                return `${totalValue}회 ${offsetText}`;
            })(),
            resolveMembershipName(item) || '-',
            `${dayjs(item.activationDate).format('YYYY-MM-DD')} ~ ${dayjs(item.expirationDate).format('YYYY-MM-DD')}`,
        ]);

        // 판매 금액 A6
        XLSX.utils.sheet_add_aoa(headerSheet, [['판매 금액']], { origin: 'A6' });
        headerSheet['A6'].s = {
            font: { bold: true, sz: 18 }, // Bold font, size 18
            alignment: { horizontal: 'center', vertical: 'center' }, // Center alignment
        };
        // Add sales list data starting from A9
        XLSX.utils.sheet_add_aoa(headerSheet, [salesListHeaders], { origin: `A7` });
        XLSX.utils.sheet_add_aoa(headerSheet, _salesListData, { origin: `A8` });
        // 판매 내역 참고 합계(결제합 − 취소합). 요약 「매출액」은 이 값이 아니다 — 그룹 매출·매출 조정이 더해진 화면 값이다.
        const salesRowCount = _salesListData?.length ?? 0;
        if (salesRowCount > 0) {
            const salesTotalRow = 8 + salesRowCount;
            const sumRangeF = `F8:F${salesTotalRow - 1}`;
            const sumRangeG = `G8:G${salesTotalRow - 1}`;
            XLSX.utils.sheet_add_aoa(
                headerSheet,
                [[isVatExclusive ? `${SALES_LIST_REFERENCE_TOTAL_LABEL} · 부가세 제외` : SALES_LIST_REFERENCE_TOTAL_LABEL]],
                { origin: `A${salesTotalRow}` },
            );
            headerSheet[`F${salesTotalRow}`] = isVatExclusive
                ? { t: 'n', f: `FLOOR((SUM(${sumRangeF}) - SUM(${sumRangeG})) / (1 + ${vatRate}/100), 1)` }
                : { t: 'n', f: `SUM(${sumRangeF}) - SUM(${sumRangeG})` };
        }

        // XLSX.utils.sheet_add_aoa(headerSheet, [['수업료 정산']], { origin: 'L6' }); // Add "수업료 정산" in L6

        // 수업료 정산 salesListData 끝나고
        XLSX.utils.sheet_add_aoa(headerSheet, [['수업료 정산']], { origin: `A${(_salesListData?.length ?? 0) + 11}` }); // Add "수업료 정산" in A11
        headerSheet[`A${(_salesListData?.length ?? 0) + 11}`].s = {
            font: { bold: true, sz: 18 }, // Bold font, size 18
            alignment: { horizontal: 'center', vertical: 'center' }, // Center
        };

        // Add class data directly below sales list
        const classDataStartRow = (_salesListData?.length ?? 0) + 12; // Add 11 to start below salesList
        XLSX.utils.sheet_add_aoa(headerSheet, [classHeaders], { origin: `A${classDataStartRow}` });
        XLSX.utils.sheet_add_aoa(headerSheet, classData, { origin: `A${classDataStartRow + 1}` });

        // Add class data with PRODUCT formula for 정산액
        classData.forEach((row: any, index: any) => {
            const rowIndex = classDataStartRow + 1 + index; // 각 데이터의 시작 행
            XLSX.utils.sheet_add_aoa(headerSheet, [row], { origin: `A${rowIndex}` });

            const resultCell = cellRef(resultIndex, rowIndex);
            const typeValue = row[typeIndex];
            const commissionValue = row[commissionIndex];
            const useServerScheduleFee =
                classDataUseServerScheduleFee[index] ||
                isSessionTier ||
                typeValue === '정액제' ||
                commissionValue === '복합';

            if (row[resultIndex] === '' || row[resultIndex] === null || row[resultIndex] === undefined) {
                return;
            }

            if (isVatExclusive) {
                const vatExclusiveCell = cellRef(vatExcludedIndex, rowIndex);
                const classFeeCell = cellRef(classFeeIndex, rowIndex);
                const comissionCell = cellRef(commissionIndex, rowIndex);

                headerSheet[vatExclusiveCell] = {
                    t: 'n',
                    f: `FLOOR(${classFeeCell}/(1+${vatRate}/100), 1)`,
                };

                if (useServerScheduleFee) {
                    headerSheet[resultCell] = { t: 'n', v: row[resultIndex] };
                } else {
                    headerSheet[resultCell] = {
                        t: 'n',
                        f: `FLOOR(PRODUCT(${vatExclusiveCell}, ${comissionCell}/100), 1)`,
                    };
                }
            } else {
                const classFeeCell = cellRef(classFeeIndex, rowIndex);
                const comissionCell = cellRef(commissionIndex, rowIndex);

                if (useServerScheduleFee) {
                    headerSheet[resultCell] = { t: 'n', v: row[resultIndex] };
                } else {
                    headerSheet[resultCell] = {
                        t: 'n',
                        f: `FLOOR(PRODUCT(${classFeeCell}, ${comissionCell}/100), 1)`,
                    };
                }
            }
        });

        // **Set Column Widths**
        headerSheet['!cols'] = Array((isVatExclusive ? 11 : 10) + (showAppliedRule ? 1 : 0)).fill({ wch: 20 });

        // VAT 안내 행 추가
        if (isVatExclusive) {
            XLSX.utils.sheet_add_aoa(headerSheet, [[`※ 부가세 미포함 기준 정산 (VAT ${vatRate}% 제외)`]], { origin: 'A5' });
        }

        // Create a workbook
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, headerSheet, 'Details');

        // Write the workbook and download
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, `${dayjs(date).format('YYYY년 MM월') + ' ' + value.coachName + '강사 급여정산'}.xlsx`);
    };

    return (
        <Col className="flex w-full h-full  relative">
            <div className={`flex w-full h-full flex-col ${selectedMenu === 'sessionReport' ? 'bg-[#F6F8FB]' : 'bg-[#F4F5F7]'} justify-center items-center mt-[70px]`}>
                <Col className={`flex ${getMenuWidth()} h-full flex-col justify-center items-center`}>
                    {selectedMenu === 'sessionReport' ? <SessionSettlementHeader onMenuClick={updateSelectedMenu}
                        onCommissionClick={openCommissionModal} onVatPolicyClick={openVatPolicyModal}
                        isRappoPaySettlementMenuVisible={isRappoPaySettlementMenuVisible} /> : <SettlementHeader
                        selectedMenu={selectedMenu}
                        onMenuClick={(type) => {
                            updateSelectedMenu(type);
                        }}
                        onCommissionClick={openCommissionModal}
                        onVatPolicyClick={openVatPolicyModal}
                        isRappoPaySettlementMenuVisible={isRappoPaySettlementMenuVisible}
                        isSessionReportMenuVisible={sessionReportMenuState === 'available'}
                    />}

                    {/* 달력 눌렀을때 */}
                    {selectedMenu === 'calendar' && (
                        <SettlementCalendar
                            date={date}
                            onClickEvent={onClickEvent}
                            onMonthNavigate={onMonthNavigate}
                            onClickCommissionModal={openCommissionModal}
                            coachList={coachList}
                            selectedCalendarCoach={selectedCalendarCoach}
                            onSelectedCalendarCoach={(coach) => {
                                if (coach.code === 'all') {
                                    setSelectedCalendarCoach('all');
                                } else {
                                    setSelectedCalendarCoach(coach);
                                }
                            }}
                            totalRemainingScheduleFee={totalRemainingScheduleFee}
                            calendar={calendar}
                            loadErrorMessage={calendarLoadError}
                            onRetry={() => refresh(5)}
                        />
                    )}
                    {/* 라포페이 정산 눌렀을때 */}
                    {isRappoPaySettlementMenuVisible && selectedMenu === 'rappoPaySettlement' && (
                        <RappoPaySettlementPage />
                    )}
                    {/* 수업료 정산 — 서버가 includeGroupClassInSettlement 토글에 따라 PT+그룹 row를 동일 shape로 섞어서 반환한다. */}
                    {selectedMenu === 'classSales' && (
                        <ClassPaySettlementPage
                            date={date}
                            onMonthNavigate={onMonthNavigate}
                            classSettlementData={classSettlementData}
                            selectedCoach={selectedCoach}
                            onCoachSelect={(coach) => setSelectedCoach(coach)}
                            onMemberScheduleRecordClick={openClassHistoryModal}
                            onMemberMembershipInfoClick={openMembershipInfoModal}
                            groupType={groupType}
                            onGroupTypeChange={(type) => setGroupType(type)}
                            selectedDate={selectedDate}
                            onChangeSelectedDate={(date) => setSelectedDate(date)}
                            totalRemainingScheduleSession={totalRemainingScheduleSession}
                            onCommissionClick={openMonthCommissionModal}
                            includeGroupClassInSettlement={includeGroupClassInSettlement}
                            loadErrorMessage={classSettlementLoadError}
                            onRetry={() => refresh(0)}
                        />
                    )}
                    {/* 매출목록 눌렀을때 */}
                    {selectedMenu === 'salesList' && (
                        <SalesListPage
                            createAlert={createAlert}
                            date={date}
                            onMonthNavigate={onMonthNavigate}
                            salesListData={salesListData}
                            selectedSalesManager={selectedSalesManager}
                            onSalesManagerSelect={(salesManager) => {
                                salesManager.code === 'all'
                                    ? setSelectedSalesManager({
                                          userId: 'all',
                                          role: 'all',
                                          userName: 'all',
                                      })
                                    : setSelectedSalesManager({
                                          userId: salesManager.code,
                                          role: salesManager.object.role,
                                          userName: salesManager.value,
                                      });
                            }}
                            selectedSalesListDate={selectedSalesListDate}
                            onChangeSelectedSalesListDate={(date) => setSelectedSalesListDate(date)}
                            onRefetchSalesRecord={refetchSalesRecord}
                        />
                    )}
                    {/* 급여 계산 눌렀을때 */}
                    {selectedMenu === 'salaryCalculation' && (
                        <SalaryCalculationPage
                            date={date}
                            onMonthNavigate={onMonthNavigate}
                            salaryCalcData={salaryCalcData}
                            coachList={coachList}
                            selectedCoach={selectedSalaryCoach}
                            onCoachSelect={(coach) => setSelectedSalaryCoach(coach)}
                            settlementType={authObject?.settlementType ?? 'base'}
                            includeGroupClassInSettlement={includeGroupClassInSettlement}
                            // 매출액, 수업료
                            refresh={() => refresh(6)}
                            loadErrorMessage={salaryLoadError}
                            onOpenCommissionSetting={() => showCommissionModal(() => refresh(6))}
                            onClickExcel={excelExport}
                        />
                    )}
                    {/* 미수금 목록 눌렀을때 */}
                    {selectedMenu === 'unpaidList' && (
                        <UnpaidListPage
                            selectedCoach={selectedUnpaidCoach}
                            onCoachSelect={setSelectedUnpaidCoach}
                            unpaidListData={unpaidListData}
                            loadErrorMessage={(refinedResults?.[7]?.error as any)?.data?.errorCode === 'UNPAID_LIST_TEMPORARILY_UNAVAILABLE' ? '잠시 후 다시 시도해 주세요' : undefined}
                            onRetry={() => refresh(7)}
                            onClickUnpaid={(row) => {
                                openPartialPaymentModal(row);
                            }}
                        />
                    )}
                    {/* 세션 현황 눌렀을때 — 자기 React Query 훅을 쓴다(위 useMultipleQueries 인덱스와 무관) */}
                    {selectedMenu === 'sessionReport' && (
                        <SessionReportPage
                            centerId={authObject?.centerId}
                            centerName={authObject?.centerName ?? authObject?.name ?? ''}
                            date={date}
                            onMonthNavigate={onMonthNavigate}
                            coachList={coachList}
                            createAlert={createAlert}
                            focus={sessionReportFocus}
                            onOpenSalary={(coachId) => {
                                // 지금 보는 달·그 강사를 이동 state 로 넘긴다(위 salaryFocus 소비 참고).
                                // 탭은 ?tab= 을 바꾸는 기존 경로라 급여 탭 잠금(2차 비밀번호) 판정도 그대로 지난다.
                                const coach = coachList?.find((item) => item.code === coachId);
                                if (!coach) return;
                                navigate(
                                    { pathname: '/settlement', search: '?tab=salaryCalculation' },
                                    { state: { salaryFocus: { yearMonth: dayjs(date).format('YYYY-MM'), coachId: coach.code } } },
                                );
                            }}
                        />
                    )}

                </Col>
            </div>

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
};

export default SettlementPage;
